import type { GameType, TriviaCategory } from 'mastodon/api_types/games';
import type {
  ApiAccountRelationshipSeveranceEventJSON,
  ApiAccountWarningJSON,
  ApiAnnualReportEventJSON,
  ApiCommunityJoinRequestNotificationPayloadJSON,
  ApiRoomInviteRoomJSON,
  BaseNotificationGroupJSON,
  ApiNotificationGroupJSON,
  ApiNotificationJSON,
  NotificationType,
  NotificationWithStatusType,
} from 'mastodon/api_types/notifications';
import type { ApiReportJSON } from 'mastodon/api_types/reports';

// Maximum number of avatars displayed in a notification group
// This corresponds to the max lenght of `group.sampleAccountIds`
export const NOTIFICATIONS_GROUP_MAX_AVATARS = 8;

interface BaseNotificationGroup extends Omit<
  BaseNotificationGroupJSON,
  'sample_account_ids'
> {
  sampleAccountIds: string[];
  partial: boolean;
}

interface BaseNotificationWithStatus<
  Type extends NotificationWithStatusType,
> extends BaseNotificationGroup {
  type: Type;
  statusId: string | undefined;
}

interface BaseNotification<
  Type extends NotificationType,
> extends BaseNotificationGroup {
  type: Type;
}

export type NotificationGroupFavourite =
  BaseNotificationWithStatus<'favourite'>;
export type NotificationGroupReblog = BaseNotificationWithStatus<'reblog'>;
export type NotificationGroupStatus = BaseNotificationWithStatus<'status'>;
export type NotificationGroupMention = BaseNotificationWithStatus<'mention'>;
export type NotificationGroupQuote = BaseNotificationWithStatus<'quote'>;
export type NotificationGroupPoll = BaseNotificationWithStatus<'poll'>;
export type NotificationGroupUpdate = BaseNotificationWithStatus<'update'>;
export type NotificationGroupQuotedUpdate =
  BaseNotificationWithStatus<'quoted_update'>;
export type NotificationGroupCommunityPost =
  BaseNotificationWithStatus<'community_post'>;
// A room in a private community posts no announcement, so its go-live
// notification has no status and carries the room (and its community) instead.
// Both optional: a public community's room still arrives as a status.
export interface NotificationGroupCommunityRoomStarted extends BaseNotificationWithStatus<'community_room_started'> {
  room?: ApiRoomInviteRoomJSON | null;
  community?: { id: string; slug: string; name: string } | null;
}
export type NotificationGroupRoomStarted =
  BaseNotificationWithStatus<'room_started'>;
// A room invite carries the room, not a status — a private room has no
// announcement post to point at. `room` is optional so a client talking to an
// older server still renders the "X invited you to a live room" line.
export interface NotificationGroupRoomInvite extends BaseNotification<'room_invite'> {
  room?: ApiRoomInviteRoomJSON | null;
}
export type NotificationGroupFollow = BaseNotification<'follow'>;
export type NotificationGroupFollowRequest = BaseNotification<'follow_request'>;
export type NotificationGroupAdminSignUp = BaseNotification<'admin.sign_up'>;

export type AccountWarningAction =
  | 'none'
  | 'disable'
  | 'mark_statuses_as_sensitive'
  | 'delete_statuses'
  | 'sensitive'
  | 'silence'
  | 'suspend';
export interface AccountWarning extends Omit<
  ApiAccountWarningJSON,
  'target_account'
> {
  targetAccountId: string;
}

export interface NotificationGroupModerationWarning extends BaseNotification<'moderation_warning'> {
  moderationWarning: AccountWarning;
}

type AccountRelationshipSeveranceEvent =
  ApiAccountRelationshipSeveranceEventJSON;
export interface NotificationGroupSeveredRelationships extends BaseNotification<'severed_relationships'> {
  event: AccountRelationshipSeveranceEvent;
}

type AnnualReportEvent = ApiAnnualReportEventJSON;
export interface NotificationGroupAnnualReport extends BaseNotification<'annual_report'> {
  annualReport: AnnualReportEvent;
}

interface Report extends Omit<ApiReportJSON, 'target_account'> {
  targetAccountId: string;
}

export interface NotificationGroupAdminReport extends BaseNotification<'admin.report'> {
  report: Report;
}

export interface NotificationGroupGameChallenge extends BaseNotification<'game_challenge'> {
  gameId: string;
  gameType: GameType;
  triviaCategory: TriviaCategory | null;
}

export interface NotificationGroupCommunityJoinRequest extends BaseNotification<'community_join_request'> {
  joinRequestId: string;
  joinRequestMessage: string | null;
  community: { id: string; slug: string; name: string };
}

export interface NotificationGroupCommunityMembershipApproved extends BaseNotification<'community_membership_approved'> {
  community: { id: string; slug: string; name: string };
}

export interface NotificationGroupCommunityInvite extends BaseNotification<'community_invite'> {
  community: { id: string; slug: string; name: string };
}

export interface NotificationGroupCommunityRemoved extends BaseNotification<'community_removed'> {
  community: { id: string; slug: string; name: string };
}

export interface NotificationGroupOrgInvite extends BaseNotification<'org_invite'> {
  organization: { id: string; slug: string; name: string };
}

export interface NotificationGroupOrgCreatorPending extends BaseNotification<'org_creator_pending'> {
  organization: { id: string; slug: string; name: string };
}

// A week's notice and a last-day notice are separate types on the server, so
// the two warnings for one pass can never collapse into a single grouped row.
// The pass is optional: an older server sends none, and the warning still reads
// correctly without the date.
export interface NotificationGroupPremiumExpiring extends BaseNotification<
  'premium_expiring' | 'premium_expiring_soon'
> {
  premiumPassExpiresAt?: string;
}

export type NotificationGroup =
  | NotificationGroupFavourite
  | NotificationGroupReblog
  | NotificationGroupStatus
  | NotificationGroupMention
  | NotificationGroupQuote
  | NotificationGroupPoll
  | NotificationGroupUpdate
  | NotificationGroupQuotedUpdate
  | NotificationGroupCommunityPost
  | NotificationGroupCommunityRoomStarted
  | NotificationGroupRoomStarted
  | NotificationGroupRoomInvite
  | NotificationGroupFollow
  | NotificationGroupFollowRequest
  | NotificationGroupModerationWarning
  | NotificationGroupSeveredRelationships
  | NotificationGroupAdminSignUp
  | NotificationGroupAdminReport
  | NotificationGroupAnnualReport
  | NotificationGroupGameChallenge
  | NotificationGroupCommunityJoinRequest
  | NotificationGroupCommunityMembershipApproved
  | NotificationGroupCommunityInvite
  | NotificationGroupCommunityRemoved
  | NotificationGroupOrgInvite
  | NotificationGroupOrgCreatorPending
  | NotificationGroupPremiumExpiring;

function createReportFromJSON(reportJSON: ApiReportJSON): Report {
  const { target_account, ...report } = reportJSON;
  return {
    targetAccountId: target_account.id,
    ...report,
  };
}

function createAccountWarningFromJSON(
  warningJSON: ApiAccountWarningJSON,
): AccountWarning {
  const { target_account, ...warning } = warningJSON;
  return {
    targetAccountId: target_account.id,
    ...warning,
  };
}

function createAccountRelationshipSeveranceEventFromJSON(
  eventJson: ApiAccountRelationshipSeveranceEventJSON,
): AccountRelationshipSeveranceEvent {
  return eventJson;
}

function createAnnualReportEventFromJSON(
  eventJson: ApiAnnualReportEventJSON,
): AnnualReportEvent {
  return eventJson;
}

export function createNotificationGroupFromJSON(
  groupJson: ApiNotificationGroupJSON,
): NotificationGroup {
  const { sample_account_ids: sampleAccountIds, ...group } = groupJson;

  switch (group.type) {
    case 'favourite':
    case 'reblog':
    case 'status':
    case 'mention':
    case 'quote':
    case 'poll':
    case 'update':
    case 'quoted_update':
    case 'community_post':
    case 'community_room_started':
    case 'room_started': {
      const { status_id: statusId, ...groupWithoutStatus } = group;
      return {
        statusId: statusId ?? undefined,
        sampleAccountIds,
        partial: false,
        ...groupWithoutStatus,
      };
    }
    case 'admin.report': {
      const { report, ...groupWithoutTargetAccount } = group;
      return {
        report: createReportFromJSON(report),
        sampleAccountIds,
        partial: false,
        ...groupWithoutTargetAccount,
      };
    }
    case 'severed_relationships':
      return {
        ...group,
        partial: false,
        event: createAccountRelationshipSeveranceEventFromJSON(group.event),
        sampleAccountIds,
      };
    case 'moderation_warning': {
      const { moderation_warning, ...groupWithoutModerationWarning } = group;
      return {
        ...groupWithoutModerationWarning,
        partial: false,
        moderationWarning: createAccountWarningFromJSON(moderation_warning),
        sampleAccountIds,
      };
    }
    case 'annual_report': {
      const { annual_report, ...groupWithoutAnnualReport } = group;
      return {
        ...groupWithoutAnnualReport,
        partial: false,
        annualReport: createAnnualReportEventFromJSON(annual_report),
        sampleAccountIds,
      };
    }
    case 'game_challenge': {
      const { game, ...groupWithoutGame } = group;
      // Defensive: API may emit `game: null` for an orphaned notification
      // (e.g. the underlying Game row was deleted but the notification row
      // wasn't). Crashing here would blank out the entire notifications
      // column. Fall back to a minimal entry so the rest still renders.
      if (!game) {
        return {
          ...groupWithoutGame,
          partial: false,
          sampleAccountIds,
          gameId: '',
          gameType: 'chess',
          triviaCategory: null,
        };
      }
      return {
        ...groupWithoutGame,
        partial: false,
        sampleAccountIds,
        gameId: game.id,
        gameType: game.game_type,
        triviaCategory: game.trivia_category,
      };
    }
    case 'room_invite': {
      // `room` is optional: an older server, or a room deleted out from under
      // its notification, yields none. The card falls back to the plain invite
      // line rather than rendering an empty shell.
      const { room, ...rest } = group;
      return {
        ...rest,
        room: room ?? null,
        sampleAccountIds,
        partial: false,
      };
    }
    case 'community_join_request': {
      const { community_join_request, ...rest } = group;
      // Defensive: see comment on `game_challenge` above. If the underlying
      // CommunityJoinRequest was deleted (e.g. approved/rejected) without
      // also destroying its notification, the API emits null here.
      if (!community_join_request) {
        return {
          ...rest,
          partial: false,
          sampleAccountIds,
          joinRequestId: '',
          joinRequestMessage: '',
          community: { id: '', slug: '', name: '' },
        };
      }
      const payload: ApiCommunityJoinRequestNotificationPayloadJSON =
        community_join_request;
      return {
        ...rest,
        partial: false,
        sampleAccountIds,
        joinRequestId: payload.id,
        joinRequestMessage: payload.message,
        community: payload.community,
      };
    }
    case 'premium_expiring':
    case 'premium_expiring_soon': {
      const { premium_pass, ...rest } = group;
      return {
        ...rest,
        partial: false,
        sampleAccountIds,
        premiumPassExpiresAt: premium_pass?.expires_at,
      };
    }
    case 'community_membership_approved':
    case 'community_invite':
    case 'community_removed': {
      const { community, ...rest } = group;
      // Defensive: see comment on game_challenge above.
      return {
        ...rest,
        partial: false,
        sampleAccountIds,
        community: community ?? { id: '', slug: '', name: '' },
      };
    }
    case 'org_invite':
    case 'org_creator_pending': {
      const { organization, ...rest } = group;
      // Defensive: see comment on game_challenge above.
      return {
        ...rest,
        partial: false,
        sampleAccountIds,
        organization: organization ?? { id: '', slug: '', name: '' },
      };
    }
    default:
      return {
        sampleAccountIds,
        partial: false,
        ...group,
      };
  }
}

export function createNotificationGroupFromNotificationJSON(
  notification: ApiNotificationJSON,
): NotificationGroup {
  const group = {
    sampleAccountIds: [notification.account.id],
    group_key: notification.group_key,
    notifications_count: 1,
    most_recent_notification_id: notification.id,
    page_min_id: notification.id,
    page_max_id: notification.id,
    latest_page_notification_at: notification.created_at,
    partial: true,
  };

  switch (notification.type) {
    case 'favourite':
    case 'reblog':
    case 'status':
    case 'mention':
    case 'quote':
    case 'poll':
    case 'update':
    case 'quoted_update':
    case 'community_post':
    case 'room_started':
      return {
        ...group,
        type: notification.type,
        statusId: notification.status?.id,
      };
    case 'community_room_started':
      return {
        ...group,
        type: notification.type,
        statusId: notification.status?.id,
        room: notification.room,
        community: notification.community,
      };
    case 'room_invite':
      return {
        ...group,
        type: notification.type,
        room: notification.room,
      };
    case 'admin.report':
      return {
        ...group,
        type: notification.type,
        report: createReportFromJSON(notification.report),
      };
    case 'severed_relationships':
      return {
        ...group,
        type: notification.type,
        event: createAccountRelationshipSeveranceEventFromJSON(
          notification.event,
        ),
      };
    case 'moderation_warning':
      return {
        ...group,
        type: notification.type,
        moderationWarning: createAccountWarningFromJSON(
          notification.moderation_warning,
        ),
      };
    case 'game_challenge':
      return {
        ...group,
        type: notification.type,
        gameId: notification.game.id,
        gameType: notification.game.game_type,
        triviaCategory: notification.game.trivia_category,
      };
    case 'community_join_request':
      return {
        ...group,
        type: notification.type,
        joinRequestId: notification.community_join_request.id,
        joinRequestMessage: notification.community_join_request.message,
        community: notification.community_join_request.community,
      };
    case 'community_membership_approved':
      return {
        ...group,
        type: notification.type,
        community: notification.community,
      };
    case 'community_invite':
    case 'community_removed':
      return {
        ...group,
        type: notification.type,
        community: notification.community,
      };
    case 'premium_expiring':
    case 'premium_expiring_soon':
      return {
        ...group,
        type: notification.type,
        premiumPassExpiresAt: notification.premium_pass?.expires_at,
      };
    case 'org_invite':
    case 'org_creator_pending':
      return {
        ...group,
        type: notification.type,
        organization: notification.organization,
      };
    default:
      return {
        ...group,
        type: notification.type,
      };
  }
}
